import { useState, useEffect } from 'react';
import type { ReadableProperty } from '@pointorama/potree-shared';

/**
 * Lightweight hook that subscribes to a ReactiveProperty and returns its current value.
 * Does NOT go through useReactivePropertyManager — no extra GraphQL queries.
 * Requires the global PropertyService to already be initialized.
 *
 * Accepts both ReactiveProperty<T> and TypedJsonProperty<T> via the ReadableProperty interface.
 *
 * @example
 * const position = usePropertyValue(getPointPositionProperty(annotationId));
 * const points = usePropertyValue(getDistancePointsProperty(annotationId));
 */
export function usePropertyValue<T>(property: ReadableProperty<T>): T {
  const [value, setValue] = useState<T>(() => property.value);

  useEffect(() => {
    // subscribe() fires immediately with current value (BehaviorSubject pattern),
    // ensuring state is in sync even if the value changed between useState init and useEffect.
    const subscription = property.subscribe((newValue) => {
      setValue(newValue);
    });

    return () => subscription.unsubscribe();
  }, [property]);

  return value;
}
